<template>
    <VTextField
        v-model="displayVal"
        :variant="variant"
        :label="label"
        :density="density"
        :hint="hint"
        :prefix="prefix"
        :suffix="suffix"
        :messages="messages"
        :disabled="disabled"
        :clearable="clearable"
        :readonly="readonly"
        :loading="loading"
        :errorMessages="errorMessages"
        v-maska="mask"
        :placeholder="placeholder"
        @blur="onBlur" />
</template>
<script lang="ts">
    import { defineComponent, ref, PropType, watch } from 'vue';
    import { VTextField } from 'vuetify/components';
    import { Variant, Density } from './DatetimeInput.interfaces';
    import { isValidDate } from '@shared/helpers/datetime/datetime';
    import {
        getLocaleIntlDatetimeFormat,
        getTranslatedDatetimeFormat,
    } from '@shared/helpers/datetime/datetime.formatters';
    import { useDate } from 'vuetify';
    import { MaskInputOptions } from 'maska';
    import { t } from '@shared/helpers/translation';

    export default defineComponent({
        name: 'DatetimeInput',
        methods: { t },
        components: { VTextField },
        emits: ['update:modelValue'],
        props: {
            modelValue: { type: String },
            label: String,
            hint: String,
            prefix: String,
            suffix: String,
            loading: { type: Boolean as PropType<boolean>, default: false },
            disabled: { type: Boolean as PropType<boolean>, default: false },
            clearable: { type: Boolean as PropType<boolean>, default: false },
            readonly: { type: Boolean as PropType<boolean>, default: false },
            messages: { type: Array as PropType<string[]> },
            errorMessages: {
                type: [Array, String] as PropType<string[] | string>,
            },
            variant: { type: String as PropType<Variant>, default: 'outlined' },
            density: { type: String as PropType<Density>, default: 'default' },
        },
        setup(props, { emit }) {
            const dateAdapter = useDate();
            const isoFormat = 'yyyy-MM-ddTHH:mm';
            const format = getLocaleIntlDatetimeFormat(dateAdapter.locale);
            const placeholder = getTranslatedDatetimeFormat(format);
            const displayVal = ref<string | undefined>();
            const mask = ref<MaskInputOptions>({
                mask: format.replace(/[a-zA-Zå]/g, '#'),
                eager: true,
            });

            const validatePartsAndLength = (dateStr: string): boolean => {
                const parts = dateStr.match(/(\d+)/g);

                return (
                    Boolean(parts && parts.length === 5) &&
                    Boolean(parts && parts.join('').length === 12) &&
                    dateStr.length >= 16
                );
            };

            const onBlur = () => {
                onUpdateDatetime(displayVal.value);
            };

            const onUpdateDatetime = (date?: string) => {
                if (date && validatePartsAndLength(date)) {
                    emit('update:modelValue', getParsedDatetime(date, format, isoFormat));
                } else {
                    emit('update:modelValue', undefined);
                }
            };

            function getParsedDatetime(
                dateString: string,
                formatString: string,
                outputFormatString: string
            ): string | undefined {
                function addLeadingZero(str: string): string {
                    return str.padStart(2, '0');
                }

                const formatParts = formatString.match(/(yyyy|MM|dd|HH|mm|.)/g);
                const dateParts: { [key: string]: string } = {};
                let currentIndex = 0;

                if (!formatParts) {
                    return undefined;
                }

                formatParts.forEach((part) => {
                    const partLength =
                        part === 'yyyy' ||
                        part === 'MM' ||
                        part === 'dd' ||
                        part === 'HH' ||
                        part === 'mm'
                            ? part.length
                            : 1;
                    dateParts[part] = dateString.substring(
                        currentIndex,
                        currentIndex + partLength
                    );
                    currentIndex += partLength;
                });

                if (
                    !dateParts['yyyy'] ||
                    !dateParts['MM'] ||
                    !dateParts['dd'] ||
                    !dateParts['HH'] ||
                    !dateParts['mm']
                ) {
                    return undefined;
                }

                const dateStr = `${dateParts['yyyy']}-${addLeadingZero(dateParts['MM'])}-${addLeadingZero(dateParts['dd'])}T${addLeadingZero(dateParts['HH'])}:${addLeadingZero(dateParts['mm'])}`;

                if (isValidDate(dateStr)) {
                    let outputDate = outputFormatString;
                    outputDate = outputDate.replace('yyyy', dateParts['yyyy']);
                    outputDate = outputDate.replace('MM', addLeadingZero(dateParts['MM']));
                    outputDate = outputDate.replace('dd', addLeadingZero(dateParts['dd']));
                    outputDate = outputDate.replace('HH', addLeadingZero(dateParts['HH']));
                    outputDate = outputDate.replace('mm', addLeadingZero(dateParts['mm']));
                    return outputDate;
                }

                return undefined;
            }

            watch(
                () => displayVal.value,
                (newValue) => {
                    onUpdateDatetime(newValue);
                }
            );

            watch(
                () => props.modelValue,
                (newValue, prevValue) => {
                    if (newValue && newValue !== prevValue) {
                        displayVal.value = getParsedDatetime(newValue, isoFormat, format);
                    }
                },
                { immediate: true }
            );

            return {
                displayVal,
                onBlur,
                mask,
                placeholder,
            };
        },
    });
</script>
